import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  CreditCard, 
  Receipt, 
  Calendar, 
  Download, 
  Upload, 
  Filter, 
  Search, 
  Eye, 
  Edit, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Zap,
  FileText,
  BarChart3,
  PieChart,
  Settings,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';

const BillingRevenueManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [billingSettings, setBillingSettings] = useState({
    currency: 'GBP',
    tax_rate: 20.0,
    auto_receipts: true,
    send_confirmation_emails: true
  });

  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    weeklyRevenue: 0,
    dailyRevenue: 0,
    revenueGrowth: 0,
    averageTransactionValue: 0,
    totalTransactions: 0,
    pendingPayments: 0,
    failedPayments: 0,
    refunds: 0
  });

  const [transactions, setTransactions] = useState([]);
  
  useEffect(() => {
    const loadTransactionsData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch("/api/charging-sessions/history", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const sessions = data.sessions || [];
          // Convert sessions to transaction format for now
          const formattedTransactions = sessions.map((session, index) => {
            const cost = parseFloat(session.total_cost || 0);
            const energy = parseFloat(session.energy_consumed || 0);
            const tax = cost * ((billingSettings.tax_rate ?? 20) / 100);
            const duration = session.duration || (session.end_time && session.start_time
              ? Math.round((new Date(session.end_time) - new Date(session.start_time)) / 60000)
              : null);
            return {
              id: `TXN-${session.id || index}`,
              sessionId: session.id,
              userId: session.user_id,
              userName: session.first_name ? `${session.first_name} ${session.last_name}` : "Unknown",
              userEmail: session.email || "unknown@example.com",
              chargerId: session.charger_id,
              chargerName: session.charger_name || session.station_name || "Unknown Station",
              amount: cost,
              totalAmount: cost + tax,
              tax: tax,
              energyDelivered: energy,
              energyConsumed: energy,
              duration: duration,
              pricePerKwh: energy > 0 ? parseFloat((cost / energy).toFixed(4)) : 0,
              status: session.status === "completed" ? "completed" : "pending",
              paymentMethod: "card",
              cardBrand: "Card",
              cardLast4: "••••",
              createdAt: session.start_time
            };
          });
          setTransactions(formattedTransactions);
        }
      } catch (error) {
        console.error("Failed to load transactions:", error);
      }
    };
    
    loadTransactionsData();
    const interval = setInterval(loadTransactionsData, 30000);
    return () => clearInterval(interval);
    // Re-runs once billingSettings.tax_rate loads (starts at the 20 default, then the real
    // saved rate once /api/billing/settings resolves) so tax isn't permanently computed at 20%.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingSettings.tax_rate]);

  useEffect(() => {
    const loadBillingData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch("/api/dashboard/stats-fixed", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const totalRevenue = data.totalRevenue || 0;

          // Real monthly/weekly/daily/growth figures — computed from actual per-day revenue,
          // not a flat 30%-of-all-time-total guess. Fetch 60 days so growth can compare the
          // most recent 30 days against the 30 days before that.
          let monthlyRevenue = 0, weeklyRevenue = 0, dailyRevenue = 0, revenueGrowth = 0;
          try {
            const revenueResponse = await fetch("/api/analytics/revenue-trend?days=60", {
              headers: { "Authorization": `Bearer ${token}` }
            });

            if (revenueResponse.ok) {
              const revData = await revenueResponse.json();
              const rows = revData.data || [];

              const todayStr = new Date().toISOString().slice(0, 10);
              const now = Date.now();
              const DAY_MS = 24 * 60 * 60 * 1000;

              rows.forEach(row => {
                const rowDate = new Date(row.date);
                const revenue = parseFloat(row.revenue) || 0;
                const ageDays = (now - rowDate.getTime()) / DAY_MS;

                if (row.date.slice(0, 10) === todayStr) dailyRevenue += revenue;
                if (ageDays <= 7) weeklyRevenue += revenue;
                if (ageDays <= 30) monthlyRevenue += revenue;
              });

              const previous30 = rows.reduce((sum, row) => {
                const ageDays = (now - new Date(row.date).getTime()) / DAY_MS;
                return ageDays > 30 && ageDays <= 60 ? sum + (parseFloat(row.revenue) || 0) : sum;
              }, 0);
              revenueGrowth = previous30 > 0 ? ((monthlyRevenue - previous30) / previous30) * 100 : 0;

              const revenueChart = rows
                .filter(row => {
                  const ageDays = (now - new Date(row.date).getTime()) / DAY_MS;
                  return ageDays <= 30;
                })
                .map(d => ({
                  date: new Date(d.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
                  amount: parseFloat(d.revenue)
                }));

              setChartData(prev => ({ ...prev, revenue: revenueChart }));
            }
          } catch (chartError) {
            console.error("Failed to load revenue chart:", chartError);
          }

          setRevenueData({
            totalRevenue,
            monthlyRevenue,
            weeklyRevenue,
            dailyRevenue,
            revenueGrowth,
            averageTransactionValue: totalRevenue > 0 ? totalRevenue / Math.max(1, data.totalSessions || 1) : 0,
            totalTransactions: data.totalSessions || 0,
            pendingPayments: 0,
            failedPayments: 0,
            refunds: 0
          });

          // Set payment methods from already-loaded revenue data
          if (totalRevenue > 0) {
            setChartData(prev => ({
              ...prev,
              paymentMethods: [{ method: "Card", amount: totalRevenue, percentage: 100 }]
            }));
          }
        }
      } catch (error) {
        console.error("Failed to load billing data:", error);
      }
    };
    loadBillingData();
  }, []);

  const [chartData, setChartData] = useState({
    revenue: [],
    paymentMethods: []
  });

  // Load billing settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch("/api/billing/settings", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.settings) setBillingSettings(data.settings);
        }
      } catch (error) {
        console.error("Failed to load billing settings:", error);
      }
    };
    loadSettings();
  }, []);

  // Receipt handlers
  const handleDownloadReceipt = async (sessionId) => {
    setReceiptLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/api/receipts/${sessionId}/download`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `LMPEnergy-Receipt-${sessionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to generate receipt');
      }
    } catch (error) {
      console.error("Receipt download error:", error);
      alert('Failed to download receipt');
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleSendReceipt = async (sessionId, email) => {
    setReceiptLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/api/receipts/${sessionId}/email`, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        alert('Receipt sent successfully to ' + (email || 'customer'));
      } else {
        alert('Failed to send receipt: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error("Receipt email error:", error);
      alert('Failed to send receipt');
    } finally {
      setReceiptLoading(false);
    }
  };

  // Report handlers
  const handleGenerateReport = async (type, format = 'csv') => {
    try {
      const token = localStorage.getItem("authToken");
      let url = `/api/reports/${type}?format=${format}`;
      if (reportDateFrom) url += `&from=${reportDateFrom}`;
      if (reportDateTo) url += `&to=${reportDateTo}`;
      const response = await fetch(url, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ext = format === 'csv' ? 'csv' : 'pdf';
        a.download = `LMPEnergy-${type.charAt(0).toUpperCase() + type.slice(1)}-Report.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error("Report generation error:", error);
      alert('Failed to generate report');
    }
  };

  // Settings handler
  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsMessage('');
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch("/api/billing/settings", {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(billingSettings)
      });
      const data = await response.json();
      if (data.success) {
        setSettingsMessage('Settings saved successfully');
        setTimeout(() => setSettingsMessage(''), 3000);
      } else {
        setSettingsMessage('Failed to save settings');
      }
    } catch (error) {
      console.error("Settings save error:", error);
      setSettingsMessage('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Export transactions as CSV
  const handleExportTransactions = () => {
    const headers = ['Transaction ID', 'Session ID', 'Customer', 'Email', 'Charger', 'Amount (GBP)', 'Energy (kWh)', 'Status', 'Date'];
    const csvRows = [headers.join(',')];
    filteredTransactions.forEach(t => {
      csvRows.push([
        t.id,
        t.sessionId,
        '"' + t.userName + '"',
        t.userEmail,
        '"' + t.chargerName + '"',
        t.amount.toFixed(2),
        t.energyDelivered.toFixed(2),
        t.status,
        t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB') : ''
      ].join(','));
    });
    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'LMPEnergy-Transactions.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'green', icon: CheckCircle, text: 'Completed' },
      pending: { color: 'yellow', icon: Clock, text: 'Pending' },
      failed: { color: 'red', icon: XCircle, text: 'Failed' },
      refunded: { color: 'gray', icon: RefreshCw, text: 'Refunded' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const MetricCard = ({ title, value, change, icon: Icon, color = 'blue', format = 'currency' }) => {
    const formatValue = (val) => {
      if (format === 'currency') return formatCurrency(val);
      if (format === 'percentage') return `${val.toFixed(1)}%`;
      if (format === 'number') return val.toString();
      return val;
    };

    const isPositive = change >= 0;
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600'
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          {change !== undefined && (
            <div className={`flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change).toFixed(1)}%
            </div>
          )}
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
          <p className="text-2xl font-bold text-gray-900">{formatValue(value)}</p>
        </div>
      </div>
    );
  };

  const RevenueChart = () => {
    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
            <p className="text-sm font-medium text-gray-900">{label}</p>
            <p className="text-sm text-blue-600">{formatCurrency(payload[0].value)}</p>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
        {chartData.revenue.length > 0 ? (
          <ResponsiveContainer width="100%" height={256}>
            <BarChart data={chartData.revenue} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} tickFormatter={(v) => '£' + v.toFixed(0)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">No revenue data available</div>
        )}
      </div>
    );
  };

  const PaymentMethodsChart = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
      <div className="space-y-4">
        {chartData.paymentMethods.map((method, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-sm font-medium text-gray-900">{method.method}</span>
            </div>
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                <div 
                  className="h-2 bg-blue-500 rounded-full" 
                  style={{ width: `${method.percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 w-16 text-right">
                {formatCurrency(method.amount)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const TransactionModal = () => {
    if (!selectedTransaction) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
            <button 
              onClick={() => setShowTransactionModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Transaction Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Session ID</label>
                      <p className="mt-1 text-sm text-gray-900">#{selectedTransaction.sessionId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">
                        {getStatusBadge(selectedTransaction.status)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTransaction.createdAt)}</p>
                    </div>
                    {selectedTransaction.paidAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Paid</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTransaction.paidAt)}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.userName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.userEmail}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">User ID</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.userId}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Charging Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Charger</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.chargerName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Duration</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.duration} minutes</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Energy Consumed</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedTransaction.energyConsumed} kWh</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Rate</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedTransaction.pricePerKwh)}/kWh</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                      <div className="mt-1 flex items-center">
                        <CreditCard className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 capitalize">
                          {selectedTransaction.cardBrand} •••• {selectedTransaction.cardLast4}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subtotal</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedTransaction.amount)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax (20%)</label>
                      <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedTransaction.tax)}</p>
                    </div>
                    <div className="border-t pt-3">
                      <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                      <p className="mt-1 text-lg font-bold text-gray-900">{formatCurrency(selectedTransaction.totalAmount)}</p>
                    </div>
                    {selectedTransaction.stripePaymentIntentId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Stripe Payment ID</label>
                        <p className="mt-1 text-xs text-gray-500 font-mono">{selectedTransaction.stripePaymentIntentId}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTransaction.failureReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                      <span className="text-sm font-medium text-red-900">Payment Failed</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1 capitalize">
                      {selectedTransaction.failureReason.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
              <div className="flex space-x-3">
                <button 
                  onClick={() => handleDownloadReceipt(selectedTransaction.sessionId)}
                  disabled={receiptLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  {receiptLoading ? 'Generating...' : 'Download Receipt'}
                </button>
                <button 
                  onClick={() => handleSendReceipt(selectedTransaction.sessionId, selectedTransaction.userEmail)}
                  disabled={receiptLoading}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {receiptLoading ? 'Sending...' : 'Send Receipt'}
                </button>
              </div>
              {selectedTransaction.status === 'failed' && (
                <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Payment
                </button>
              )}
              {selectedTransaction.status === 'completed' && (
                <button className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Process Refund
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = searchTerm === '' || 
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.chargerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || transaction.status === filterStatus;
    
    const matchesDate = !filterDateFrom || !transaction.createdAt || 
      new Date(transaction.createdAt) >= new Date(filterDateFrom);
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Billing & Revenue</h1>
              <p className="text-sm text-gray-600">Financial management and analytics</p>
            </div>
            <div className="flex items-center space-x-4">
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
              <button 
                onClick={() => setActiveTab('reports')}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-200 mb-8">
          <nav className="flex space-x-8">
            {['overview', 'transactions', 'reports', 'settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Revenue Metrics */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                title="Total Revenue"
                value={revenueData.totalRevenue}
                change={revenueData.revenueGrowth}
                icon={DollarSign}
                color="green"
              />
              <MetricCard
                title="Monthly Revenue"
                value={revenueData.monthlyRevenue}
                change={8.3}
                icon={TrendingUp}
                color="blue"
              />
              <MetricCard
                title="Average Transaction"
                value={revenueData.averageTransactionValue}
                change={-2.1}
                icon={Receipt}
                color="purple"
              />
              <MetricCard
                title="Total Transactions"
                value={revenueData.totalTransactions}
                change={15.7}
                icon={CreditCard}
                color="orange"
                format="number"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <RevenueChart />
              <PaymentMethodsChart />
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Pending Payments</h3>
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(revenueData.pendingPayments)}</p>
                <p className="text-sm text-gray-600 mt-1">Awaiting processing</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Failed Payments</h3>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(revenueData.failedPayments)}</p>
                <p className="text-sm text-gray-600 mt-1">Require attention</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Refunds</h3>
                  <RefreshCw className="w-5 h-5 text-gray-500" />
                </div>
                <p className="text-2xl font-bold text-gray-600">{formatCurrency(revenueData.refunds)}</p>
                <p className="text-sm text-gray-600 mt-1">This period</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button 
                  onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterDateFrom(''); }}
                  className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Transactions</h3>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={handleExportTransactions}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </button>
                  <button 
                    onClick={() => window.location.reload()}
                    className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                
<table className="min-w-full divide-y divide-gray-200">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Transaction
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Customer
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Charger
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Amount
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Status
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Date
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Actions
                     </th>
                   </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-gray-200">
                   {filteredTransactions.map((transaction) => (
                     <tr key={transaction.id} className="hover:bg-gray-50">
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div>
                           <div className="text-sm font-medium text-gray-900">{transaction.id}</div>
                           <div className="text-sm text-gray-500">Session #{transaction.sessionId}</div>
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div>
                           <div className="text-sm font-medium text-gray-900">{transaction.userName}</div>
                           <div className="text-sm text-gray-500">{transaction.userEmail}</div>
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="text-sm text-gray-900">{transaction.chargerName}</div>
                         <div className="text-sm text-gray-500">{transaction.energyConsumed} kWh • {transaction.duration}m</div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="text-sm font-medium text-gray-900">{formatCurrency(transaction.totalAmount)}</div>
                         <div className="text-sm text-gray-500">
                           <CreditCard className="inline w-3 h-3 mr-1" />
                           {transaction.cardBrand} •••• {transaction.cardLast4}
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         {getStatusBadge(transaction.status)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                         {formatDate(transaction.createdAt)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                         <button
                           onClick={() => {
                             setSelectedTransaction(transaction);
                             setShowTransactionModal(true);
                           }}
                           className="text-blue-600 hover:text-blue-900 mr-3"
                         >
                           <Eye className="w-4 h-4" />
                         </button>
                         <button className="text-gray-400 hover:text-gray-600">
                           <Download className="w-4 h-4" />
                         </button>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
           </div>
         </div>
       )}

       {activeTab === 'reports' && (
         <div className="space-y-6">
           {/* Date Range Filter */}
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Report Date Range</h3>
             <p className="text-sm text-gray-500 mb-4">Select a date range to filter reports. Leave blank for all time.</p>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                 <input
                   type="date"
                   value={reportDateFrom}
                   onChange={(e) => setReportDateFrom(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                 <input
                   type="date"
                   value={reportDateTo}
                   onChange={(e) => setReportDateTo(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                 />
               </div>
               <div className="flex items-end">
                 <button
                   onClick={() => { setReportDateFrom(''); setReportDateTo(''); }}
                   className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                 >
                   Clear Dates
                 </button>
               </div>
             </div>
           </div>

           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Reports</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
               <div className="p-4 border border-gray-200 rounded-lg">
                 <div className="flex items-center mb-3">
                   <FileText className="w-8 h-8 text-blue-500 mr-3" />
                   <div>
                     <p className="text-sm font-medium text-gray-900">Revenue Report</p>
                     <p className="text-sm text-gray-500">Detailed revenue breakdown</p>
                   </div>
                 </div>
                 <div className="flex space-x-2">
                   <button onClick={() => handleGenerateReport('revenue', 'csv')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">
                     <Download className="w-3 h-3 mr-1" /> CSV
                   </button>
                   <button onClick={() => handleGenerateReport('revenue', 'pdf')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">
                     <Download className="w-3 h-3 mr-1" /> PDF
                   </button>
                 </div>
               </div>
               <div className="p-4 border border-gray-200 rounded-lg">
                 <div className="flex items-center mb-3">
                   <BarChart3 className="w-8 h-8 text-green-500 mr-3" />
                   <div>
                     <p className="text-sm font-medium text-gray-900">Transaction Report</p>
                     <p className="text-sm text-gray-500">Transaction analytics</p>
                   </div>
                 </div>
                 <div className="flex space-x-2">
                   <button onClick={() => handleGenerateReport('transactions', 'csv')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100">
                     <Download className="w-3 h-3 mr-1" /> CSV
                   </button>
                   <button onClick={() => handleGenerateReport('transactions', 'pdf')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100">
                     <Download className="w-3 h-3 mr-1" /> PDF
                   </button>
                 </div>
               </div>
               <div className="p-4 border border-gray-200 rounded-lg">
                 <div className="flex items-center mb-3">
                   <PieChart className="w-8 h-8 text-purple-500 mr-3" />
                   <div>
                     <p className="text-sm font-medium text-gray-900">Tax Report</p>
                     <p className="text-sm text-gray-500">VAT and tax summary</p>
                   </div>
                 </div>
                 <div className="flex space-x-2">
                   <button onClick={() => handleGenerateReport('tax', 'csv')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100">
                     <Download className="w-3 h-3 mr-1" /> CSV
                   </button>
                   <button onClick={() => handleGenerateReport('tax', 'pdf')} className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100">
                     <Download className="w-3 h-3 mr-1" /> PDF
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

       {activeTab === 'settings' && (
         <div className="space-y-6">
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Settings</h3>
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700">Default Currency</label>
                 <select 
                   value={billingSettings.currency}
                   onChange={(e) => setBillingSettings({...billingSettings, currency: e.target.value})}
                   className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                 >
                   <option value="GBP">British Pound (GBP)</option>
                   <option value="EUR">Euro (EUR)</option>
                   <option value="USD">US Dollar (USD)</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700">Tax Rate (%)</label>
                 <input
                   type="number"
                   step="0.1"
                   value={billingSettings.tax_rate}
                   onChange={(e) => setBillingSettings({...billingSettings, tax_rate: parseFloat(e.target.value) || 0})}
                   className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                 />
               </div>
               <div className="flex items-center">
                 <input
                   type="checkbox"
                   checked={billingSettings.auto_receipts}
                   onChange={(e) => setBillingSettings({...billingSettings, auto_receipts: e.target.checked})}
                   className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                 />
                 <label className="ml-2 block text-sm text-gray-900">
                   Automatic receipt generation
                 </label>
               </div>
               <div className="flex items-center">
                 <input
                   type="checkbox"
                   checked={billingSettings.send_confirmation_emails}
                   onChange={(e) => setBillingSettings({...billingSettings, send_confirmation_emails: e.target.checked})}
                   className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                 />
                 <label className="ml-2 block text-sm text-gray-900">
                   Send payment confirmation emails
                 </label>
               </div>
             </div>
             <div className="mt-6 flex items-center space-x-4">
               <button 
                 onClick={handleSaveSettings}
                 disabled={settingsSaving}
                 className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
               >
                 <Settings className="w-4 h-4 mr-2" />
                 {settingsSaving ? 'Saving...' : 'Save Settings'}
               </button>
               {settingsMessage && (
                 <span className={`text-sm ${settingsMessage.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                   {settingsMessage}
                 </span>
               )}
             </div>
           </div>
         </div>
       )}
     </div>

     {/* Transaction Detail Modal */}
     {showTransactionModal && <TransactionModal />}
   </div>
 );
};

export default BillingRevenueManagement;
